/**
 * @module Zod v4 schemas for AI SDK UI message types
 * @see https://github.com/vercel/ai/blob/c123363/packages/ai/src/ui/ui-messages.ts
 *
 * Non-generic runtime schemas for the AI SDK's exported UIMessage types.
 * Generic type parameters are represented as z.unknown().
 */
import * as z from "zod/v4";

// -- Provider Metadata --

export const ProviderMetadataSchema = z.record(
  z.string(),
  z.record(z.string(), z.unknown()),
);

export type ProviderMetadata = z.infer<typeof ProviderMetadataSchema>;

// -- UIDataTypes, UITool, UITools --

export const UIDataTypesSchema = z.record(z.string(), z.unknown());

export type UIDataTypes = z.infer<typeof UIDataTypesSchema>;

export const UIToolSchema = z.object({
  input: z.unknown(),
  output: z.unknown().optional(),
});

export type UITool = z.infer<typeof UIToolSchema>;

export const UIToolsSchema = z.record(z.string(), UIToolSchema);

export type UITools = z.infer<typeof UIToolsSchema>;

// -- Simple Part Schemas --

export const TextUIPartSchema = z.object({
  type: z.literal("text"),
  text: z.string(),
  state: z.enum(["streaming", "done"]).optional(),
  providerMetadata: ProviderMetadataSchema.optional(),
});

export type TextUIPart = z.infer<typeof TextUIPartSchema>;

export const ReasoningUIPartSchema = z.object({
  type: z.literal("reasoning"),
  text: z.string(),
  state: z.enum(["streaming", "done"]).optional(),
  providerMetadata: ProviderMetadataSchema.optional(),
});

export type ReasoningUIPart = z.infer<typeof ReasoningUIPartSchema>;

export const SourceUrlUIPartSchema = z.object({
  type: z.literal("source-url"),
  sourceId: z.string(),
  url: z.string(),
  title: z.string().optional(),
  providerMetadata: ProviderMetadataSchema.optional(),
});

export type SourceUrlUIPart = z.infer<typeof SourceUrlUIPartSchema>;

export const SourceDocumentUIPartSchema = z.object({
  type: z.literal("source-document"),
  sourceId: z.string(),
  mediaType: z.string(),
  title: z.string(),
  filename: z.string().optional(),
  providerMetadata: ProviderMetadataSchema.optional(),
});

export type SourceDocumentUIPart = z.infer<typeof SourceDocumentUIPartSchema>;

export const FileUIPartSchema = z.object({
  type: z.literal("file"),
  mediaType: z.string(),
  filename: z.string().optional(),
  url: z.string(),
  providerMetadata: ProviderMetadataSchema.optional(),
});

export type FileUIPart = z.infer<typeof FileUIPartSchema>;

export const StepStartUIPartSchema = z.object({
  type: z.literal("step-start"),
});

export type StepStartUIPart = z.infer<typeof StepStartUIPartSchema>;

export const DataUIPartSchema = z.object({
  type: z.string().regex(/^data-.+/),
  id: z.string().optional(),
  data: z.unknown(),
});

export type DataUIPart = z.infer<typeof DataUIPartSchema>;

// -- Approval Sub-schemas --

const ApprovalRequestedSchema = z.object({
  id: z.string(),
});

const ApprovalRespondedSchema = z.object({
  id: z.string(),
  approved: z.boolean(),
  reason: z.string().optional(),
});

const ApprovalApprovedSchema = z.object({
  id: z.string(),
  approved: z.literal(true),
  reason: z.string().optional(),
});

const ApprovalDeniedSchema = z.object({
  id: z.string(),
  approved: z.literal(false),
  reason: z.string().optional(),
});

// -- Tool Invocation State Variants --

const invocationFields = {
  toolCallId: z.string(),
  title: z.string().optional(),
  providerExecuted: z.boolean().optional(),
};

const StateInputStreaming = z.object({
  ...invocationFields,
  state: z.literal("input-streaming"),
  input: z.unknown().optional(),
  callProviderMetadata: ProviderMetadataSchema.optional(),
});

const StateInputAvailable = z.object({
  ...invocationFields,
  state: z.literal("input-available"),
  input: z.unknown(),
  callProviderMetadata: ProviderMetadataSchema.optional(),
});

const StateApprovalRequested = z.object({
  ...invocationFields,
  state: z.literal("approval-requested"),
  input: z.unknown(),
  callProviderMetadata: ProviderMetadataSchema.optional(),
  approval: ApprovalRequestedSchema,
});

const StateApprovalResponded = z.object({
  ...invocationFields,
  state: z.literal("approval-responded"),
  input: z.unknown(),
  callProviderMetadata: ProviderMetadataSchema.optional(),
  approval: ApprovalRespondedSchema,
});

const StateOutputAvailable = z.object({
  ...invocationFields,
  state: z.literal("output-available"),
  input: z.unknown(),
  output: z.unknown(),
  callProviderMetadata: ProviderMetadataSchema.optional(),
  preliminary: z.boolean().optional(),
  approval: ApprovalApprovedSchema.optional(),
});

const StateOutputError = z.object({
  ...invocationFields,
  state: z.literal("output-error"),
  input: z.unknown().optional(),
  rawInput: z.unknown().optional(),
  errorText: z.string(),
  callProviderMetadata: ProviderMetadataSchema.optional(),
  approval: ApprovalApprovedSchema.optional(),
});

const StateOutputDenied = z.object({
  ...invocationFields,
  state: z.literal("output-denied"),
  input: z.unknown(),
  callProviderMetadata: ProviderMetadataSchema.optional(),
  approval: ApprovalDeniedSchema,
});

const stateVariants = [
  StateInputStreaming,
  StateInputAvailable,
  StateApprovalRequested,
  StateApprovalResponded,
  StateOutputAvailable,
  StateOutputError,
  StateOutputDenied,
] as const;

// -- UIToolInvocation --

export const UIToolInvocationSchema = z.union(stateVariants);

export type UIToolInvocation = z.infer<typeof UIToolInvocationSchema>;

// -- ToolUIPart --

const toolTypeField = { type: z.string().regex(/^tool-.+/) };

export const ToolUIPartSchema = z.union([
  StateInputStreaming.extend(toolTypeField),
  StateInputAvailable.extend(toolTypeField),
  StateApprovalRequested.extend(toolTypeField),
  StateApprovalResponded.extend(toolTypeField),
  StateOutputAvailable.extend(toolTypeField),
  StateOutputError.extend(toolTypeField),
  StateOutputDenied.extend(toolTypeField),
]);

export type ToolUIPart = z.infer<typeof ToolUIPartSchema>;

// -- DynamicToolUIPart --

const dynamicToolFields = {
  type: z.literal("dynamic-tool"),
  toolName: z.string(),
};

export const DynamicToolUIPartSchema = z.union([
  StateInputStreaming.extend(dynamicToolFields),
  StateInputAvailable.extend(dynamicToolFields),
  StateApprovalRequested.extend(dynamicToolFields),
  StateApprovalResponded.extend(dynamicToolFields),
  StateOutputAvailable.extend(dynamicToolFields),
  StateOutputError.extend(dynamicToolFields),
  StateOutputDenied.extend(dynamicToolFields),
]);

export type DynamicToolUIPart = z.infer<typeof DynamicToolUIPartSchema>;

// -- UIMessagePart --

export const UIMessagePartSchema = z.union([
  TextUIPartSchema,
  ReasoningUIPartSchema,
  ToolUIPartSchema,
  DynamicToolUIPartSchema,
  SourceUrlUIPartSchema,
  SourceDocumentUIPartSchema,
  FileUIPartSchema,
  DataUIPartSchema,
  StepStartUIPartSchema,
]);

export type UIMessagePart = z.infer<typeof UIMessagePartSchema>;

// -- UIMessage --

export const UIMessageSchema = z.object({
  id: z.string(),
  role: z.enum(["system", "user", "assistant"]),
  metadata: z.unknown().optional(),
  parts: z.array(UIMessagePartSchema),
});

export type UIMessage = z.infer<typeof UIMessageSchema>;
